'use client'
import { Assessments } from '@crystal-eyes/types'
import SectionCard from '../../components/SectionCard'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import styles from './Strengths.module.scss'
import assessmentStyles from './Assessments.module.scss'
import Link from 'next/link'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import useTakingAssessment from '../../hooks/useTakingAssessment'

interface Identity {
  id: string
}

export default function Strengths({
  identity,
  assessments,
}: {
  identity: Identity
  assessments?: Assessments
}) {
  const { data: isOwnProfile } = useIsOwnProfile({ identity })

  const strengthTraits = assessments?.strengths?.traits

  const {
    assessmentState: { setTaking: setTakingAssessment },
  } = useTakingAssessment()

  if (!isOwnProfile && !strengthTraits) return null

  return (
    <SectionCard
      cardId="strengths_assessment"
      icon={Icons.Dumbbell}
      title="Strengths"
      showPrint={true}
      className={`${assessmentStyles.assessment} ${styles.strengths}`}
    >
      {!strengthTraits && (
        <div className="take-assessment-cta">
          <div className="visualization">
            <div className="pills">
              <div className="strength-line">
                <span>1</span>
                <div className="pill" />
              </div>

              <div className="strength-line">
                <span>2</span>
                <div className="pill" />
              </div>

              <div className="strength-line">
                <span>3</span>
                <div className="pill" />
              </div>
            </div>
          </div>

          <div className="cta">
            <h4>Find out your Strengths</h4>

            <p>
              Answer a few additional questions to learn more about your
              Strengths.
            </p>

            <button
              className="CE action-button"
              onClick={evt => {
                evt.preventDefault()
                setTakingAssessment('strengths')
              }}
            >
              Start Test
            </button>
            <br />
          </div>
        </div>
      )}

      {strengthTraits && (
        <>
          <div className="assessment-results">
            {strengthTraits.map(trait => (
              <div className="trait" key={trait.name}>
                <div className="trait-icon CE Parented disc-color">
                  <Icon icon={STRENGTH_ICONS[trait.name] || Icons.Weight} />
                </div>

                <div className="overview">
                  <h4 className="CE Parented disc-color">{trait.name}</h4>
                  <p>{trait.description}</p>
                </div>
              </div>
            ))}
          </div>

          {isOwnProfile && (
            <div className="retake-cta">
              <Link
                href="#"
                onClick={evt => {
                  evt.preventDefault()
                  setTakingAssessment('strengths')
                }}
              >
                Retake the Strengths Test
              </Link>
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

const STRENGTH_ICONS: { [key: string]: Icons } = {
  Creativity: Icons.Paint,
  Curiosity: Icons.Idea,
  'Judgement/Critical Thinking': Icons.Gavel,
  'Love of Learning': Icons.BooksApple,
  'Perspective/Wisdom': Icons.Brain,
  Courage: Icons.Lion,
  Perseverance: Icons.Mountain,
  Honesty: Icons.TwoFingersUp,
  Zest: Icons.Tada,
  Love: Icons.HeartArrow,
  Kindness: Icons.HandHeart,
  'Social Intelligence': Icons.Conversation,
  Teamwork: Icons.Group,
  Fairness: Icons.Scale,
  Leadership: Icons.GroupThink,
  'Forgiveness/Mercy': Icons.Branch,
  'Humility/Modesty': Icons.HandHeart,
  Prudence: Icons.Warning,
  'Self-Regulation': Icons.Whistle,
  'Appreciation of Beauty and Excellence': Icons.Diamond,
  Gratitude: Icons.HeartFlower,
  Hope: Icons.GlassHalfFull,
  Humor: Icons.Smile,
  'Spirituality/Sense of Meaning': Icons.Flower,
}
